import React, { FC } from 'react';
import { useLocale } from '@app/core/hooks/useLocale';
import {
  amountRules,
  calculatedMultiplierRules,
  cellValueFormattingRules,
  enteredPercentageViewRules,
} from '@app/shared/components/cell-value/CellValueConfigurations';
import styles from '../../opm-summary-waterfall-erf.module.scss';
import classNames from 'classnames';
import {
  OpmSampleWaterfallsDto,
  OpmSampleWaterfallInstrumentDto,
} from '@app/shared/models/contracts/opm-calculation-results-dto';
import SvgChevron from '@app/shared/icons/Chevron';
import { ExpandableKeys, ExpandedSectionsState } from './SampleWaterfallOutputsSummary';
import { getCalculatedProjectValueInPercent } from '@app/shared/formatters';
import { NOT_APPLICABLE } from './sample-waterfall-column-helpers';
import WaterfallValueCell from './WaterfallValueCell';

type AggregateValueKeys = Omit<OpmSampleWaterfallsDto, 'instruments'>;
type InstrumentValueKeys = Omit<OpmSampleWaterfallInstrumentDto, 'tranches'>;

interface SponsorReturnSectionProps {
  waterfallResults: Dictionary<OpmSampleWaterfallsDto>;
  columnKeys: string[];
  allInstruments: (OpmSampleWaterfallInstrumentDto & { instrumentId: string })[];
  onToggle: (key: string, subKey?: string) => void;
  expandedSections: ExpandedSectionsState;
}

const valueTransformer = (
  value: Nullable<number> | boolean | string,
  rules: cellValueFormattingRules
) =>
  typeof value === 'number' && rules === enteredPercentageViewRules
    ? getCalculatedProjectValueInPercent(value)
    : value;

const SampleWaterfallOutputsSponsorReturnSection: FC<SponsorReturnSectionProps> = ({
  waterfallResults,
  columnKeys,
  allInstruments,
  onToggle,
  expandedSections,
}) => {
  const { l } = useLocale();

  const sponsorInstruments = allInstruments.filter((instrument) =>
    columnKeys.some((columnKey) => {
      const scenarioInstrument =
        waterfallResults[columnKey]?.instruments?.[instrument.instrumentId];
      return (
        scenarioInstrument &&
        (scenarioInstrument.sponsorDistributions !== 0 ||
          scenarioInstrument.initialSponsorInvestment !== 0)
      );
    })
  );

  const createAggregateRow = (
    label: string,
    valueKey: keyof AggregateValueKeys,
    rules = amountRules
  ) => (
    <tr key={`aggregate-${valueKey}`}>
      <td className={classNames(styles['instrument-indentation'], styles['border-right'])}>
        {label}
      </td>
      {columnKeys.map((key) => {
        const result = waterfallResults[key];
        return (
          <WaterfallValueCell
            key={key}
            value={result ? valueTransformer(result[valueKey], rules) : NOT_APPLICABLE}
            rules={rules}
          />
        );
      })}
    </tr>
  );

  const createInstrumentRow = (
    label: string,
    valueKey: keyof InstrumentValueKeys,
    instrumentId: string,
    rules = amountRules
  ) => {
    return (
      <tr key={`${instrumentId}-${valueKey}`}>
        <td className={classNames(styles['instrument-indentation'], styles['border-right'])}>
          {label}
        </td>
        {columnKeys.map((key) => {
          const instrument = waterfallResults[key]?.instruments?.[instrumentId];
          return (
            <WaterfallValueCell
              key={key}
              value={instrument ? valueTransformer(instrument[valueKey], rules) : NOT_APPLICABLE}
              rules={rules}
            />
          );
        })}
      </tr>
    );
  };

  const instrumentHasReturnMetrics = (instrumentId: string): boolean =>
    columnKeys.some((key) => {
      const instrumentDate = waterfallResults[key]?.instruments?.[instrumentId];
      return (
        instrumentDate?.sponsorDistributions ||
        instrumentDate?.sponsorMoM ||
        instrumentDate?.sponsorIrr ||
        instrumentDate?.totalSponsorInvestment
      );
    });

  return (
    <>
      <tr>
        <th className={styles['section-header']} colSpan={columnKeys.length + 1}>
          {!expandedSections.sponsorReturn ? (
            <div className={styles['expand-toggle']}>
              <SvgChevron
                onClick={() => onToggle(ExpandableKeys.SponsorReturn)}
                className={styles[`chevron-collapsed`]}
              />
            </div>
          ) : (
            <div className={styles['expand-toggle']}>
              <SvgChevron
                onClick={() => onToggle(ExpandableKeys.SponsorReturn)}
                className={styles[`chevron-expanded`]}
              />
            </div>
          )}
          {l('_SponsorReturnMetrics')}
        </th>
      </tr>
      {expandedSections.sponsorReturn && (
        <>
          <tr>
            <th
              className={classNames(styles['section-header'], styles['section-header--indented'])}
              colSpan={columnKeys.length + 1}>
              {!expandedSections.sponsorReturnInstruments?.['Aggregate'] ? (
                <div
                  className={classNames(
                    styles['expand-toggle'],
                    styles['expand-toggle--instrument']
                  )}>
                  <SvgChevron
                    onClick={() => onToggle(ExpandableKeys.SponsorReturnInstruments, 'Aggregate')}
                    className={styles[`chevron-collapsed`]}
                  />
                </div>
              ) : (
                <div
                  className={classNames(
                    styles['expand-toggle'],
                    styles['expand-toggle--instrument']
                  )}>
                  <SvgChevron
                    onClick={() => onToggle(ExpandableKeys.SponsorReturnInstruments, 'Aggregate')}
                    className={styles[`chevron-expanded`]}
                  />
                </div>
              )}
              {l('_Aggregate')}
            </th>
          </tr>
          {expandedSections.sponsorReturnInstruments?.['Aggregate'] && (
            <>
              {createAggregateRow(l('_TotalSponsorInvestment'), 'totalInstitutionalInvestment')}
              {createAggregateRow(l('_TotalSponsorReturn'), 'totalInstitutionalReturn')}
              {createAggregateRow(l('_SponsorMoM'), 'institutionalMoM', calculatedMultiplierRules)}
              {createAggregateRow(l('_SponsorIRR'), 'institutionalIrr', enteredPercentageViewRules)}
            </>
          )}
          {sponsorInstruments.map(
            (instrument) =>
              instrumentHasReturnMetrics(instrument.instrumentId) && (
                <React.Fragment key={instrument.instrumentId}>
                  <tr>
                    <th
                      className={classNames(
                        styles['section-header'],
                        styles['section-header--indented']
                      )}
                      colSpan={columnKeys.length + 1}>
                      {!expandedSections.sponsorReturnInstruments?.[instrument.instrumentId] ? (
                        <div
                          className={classNames(
                            styles['expand-toggle'],
                            styles['expand-toggle--instrument']
                          )}>
                          <SvgChevron
                            onClick={() =>
                              onToggle(
                                ExpandableKeys.SponsorReturnInstruments,
                                instrument.instrumentId
                              )
                            }
                            className={styles[`chevron-collapsed`]}
                          />
                        </div>
                      ) : (
                        <div
                          className={classNames(
                            styles['expand-toggle'],
                            styles['expand-toggle--instrument']
                          )}>
                          <SvgChevron
                            onClick={() =>
                              onToggle(
                                ExpandableKeys.SponsorReturnInstruments,
                                instrument.instrumentId
                              )
                            }
                            className={styles[`chevron-expanded`]}
                          />
                        </div>
                      )}
                      {instrument.instrumentName}
                    </th>
                  </tr>
                  {expandedSections.sponsorReturnInstruments?.[instrument.instrumentId] && (
                    <>
                      {createInstrumentRow(
                        l('_TotalSponsorInvestment'),
                        'totalSponsorInvestment',
                        instrument.instrumentId
                      )}
                      {createInstrumentRow(
                        l('_TotalSponsorReturn'),
                        'sponsorDistributions',
                        instrument.instrumentId
                      )}
                      {createInstrumentRow(
                        l('_SponsorMoM'),
                        'sponsorMoM',
                        instrument.instrumentId,
                        calculatedMultiplierRules
                      )}
                      {createInstrumentRow(
                        l('_SponsorIRR'),
                        'sponsorIrr',
                        instrument.instrumentId,
                        enteredPercentageViewRules
                      )}
                    </>
                  )}
                </React.Fragment>
              )
          )}
        </>
      )}
    </>
  );
};

export default SampleWaterfallOutputsSponsorReturnSection;
